import { AppContext } from "@components/features/appContext/AppContext";
import Button from "@components/primitives/form/Button";
import Icon from "@components/primitives/icon/Icon";
import Layout from "@components/primitives/layout/Layout";
import useFetchMembers from "@hooks/members/useFetchMembers";
import { appPath } from "@routes/app";
import { calculateAge } from "@utils/calculateAge";
import { config, createTitle } from "@utils/config";
import { languageOptions, translation } from "@utils/translation";
import { MemberType } from "next-lib";
import { useContext, useEffect, useState } from "react";
import { Helmet } from "react-helmet-async";
import { useNavigate, useParams } from "react-router-dom";
import { memberImage } from "../membersPage/MembersPage";

function MemberInformationPage() {
  const { memberUrl } = useParams();
  const navigate = useNavigate();
  const { language } = useContext(AppContext);
  const { members } = useFetchMembers(language);
  const [currentMember, setCurrentMember] = useState<MemberType | null>(null);

  useEffect(() => {
    if (!members) return;
    const findMember = members?.find(
      (m: MemberType) => m.url.toLowerCase() === memberUrl
    );

    if (!findMember) navigate(appPath.members);

    setCurrentMember(findMember);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [members]);

  return (
    <Layout>
      <Helmet>
        <title>{createTitle(translation(language).header.members)}</title>
      </Helmet>
      <div className="h-max bg-primary w-full pt-32 pb-20 px-5">
        <Button
          onClick={() => navigate(appPath.members)}
          className="mb-5 w-fit"
        >
          <div className="flex gap-2">
            <Icon icon="chevron-left" />
            {translation(language).general.text.back}
          </div>
        </Button>
        <div className="mx-auto grid max-w-2xl grid-cols-1 items-start gap-x-8 gap-y-16 sm:gap-y-24 lg:mx-0 lg:max-w-none lg:grid-cols-2">
          <div className="lg:pr-4">
            <div className="relative overflow-hidden rounded-3xl px-6 pb-9 pt-64 shadow-2xl sm:px-12 lg:max-w-lg lg:px-8 lg:pb-8 xl:px-10 xl:pb-10">
              <img
                className="absolute inset-0 w-full object-cover h-full"
                src={memberImage[currentMember?.firstName.toLowerCase() as any]}
                alt={currentMember?.firstName}
              />
            </div>
          </div>
          <div>
            <div className="text-base leading-7 text-secondary lg:max-w-lg">
              <h1 className="mt-2 text-3xl font-bold tracking-tight text-primary sm:text-4xl">
                {currentMember?.firstName} {currentMember?.lastName}
              </h1>
              <div className="max-w-xl">
                <p className="mt-6">
                  {translation(language).members.memberDescription(
                    currentMember?.firstName,
                    calculateAge(currentMember?.birthDate),
                    currentMember?.role[
                      languageOptions.findIndex(
                        (l: any) =>
                          l.value ===
                          localStorage.getItem(config.localStorage.language)
                      )
                    ].toLowerCase()
                  )}
                </p>
              </div>
              <div className="mt-6 flex gap-5 items-center">
                {currentMember?.socials.map(
                  (social: MemberType["socials"][0], index: number) => {
                    const icon = {
                      instagram: "instagram",
                      tiktok: "tiktok",
                      snapchat: "snapchat",
                      website: "globe",
                    } as any;

                    const iconPrefix = {
                      instagram: "fab",
                      tiktok: "fab",
                      snapchat: "fab",
                      website: "fas",
                    } as any;

                    return (
                      <Icon
                        key={index}
                        className="h-[28px] text-secondary hover:text-primary cursor-pointer transition-all"
                        prefix={iconPrefix[social.type]}
                        href={social.url}
                        icon={icon[social.type]}
                      />
                    );
                  }
                )}
              </div>
            </div>
            <dl className="mt-10 grid grid-cols-2 gap-8 border-t border-primary pt-10 sm:grid-cols-4">
              <div>
                <dt className="text-sm font-semibold leading-6 text-secondary">
                  {translation(language).members.age}
                </dt>
                <dd className="mt-2 text-3xl font-bold leading-10 tracking-tight text-primary">
                  {calculateAge(currentMember?.birthDate)}
                </dd>
              </div>
              <div>
                <dt className="text-sm font-semibold leading-6 text-secondary">
                  {translation(language).members.instrument}
                </dt>
                <dd className="mt-2 text-3xl font-bold leading-10 tracking-tight text-primary">
                  {
                    currentMember?.instrument[
                      languageOptions.findIndex(
                        (l: any) =>
                          l.value ===
                          localStorage.getItem(config.localStorage.language)
                      )
                    ]
                  }
                </dd>
              </div>
            </dl>
          </div>
        </div>
      </div>
    </Layout>
  );
}

export default MemberInformationPage;
